import type { ComposedPage, ComposedParagraph } from '@xcel-next/composer-schema'
import type { RsConfig } from '../stores/config'

// ComposedParagraph uses a strict ParagraphType enum, but this composable injects additional
// runtime-only paragraph types (adaAdSlot, adaNativeCrossbar, …) that don't exist in the schema.
// We widen paragraphType to string and add the ad-specific fields.
// No index signature here — it would collapse named property types to unknown on access.
type Paragraph = Omit<ComposedParagraph, 'paragraphType'> & {
  paragraphType: string,
  enableRectangle1?: boolean,
  listOrderPosition?: number
}

type AdSlots = {
  embedded: boolean,
  info: {
    initialParagraphCount?: number
  }
}

// ComposedPage is strict about pageMeta/body shapes; adSlots is injected at runtime
type PageData = Omit<ComposedPage, 'pageMeta' | 'body' | 'metaData'> & {
  pageMeta: Omit<ComposedPage['pageMeta'], 'pageType'> & {
    pageType: string,
    page?: number,
    adSlots?: AdSlots
  },
  body: Omit<ComposedPage['body'], 'paragraphs'> & {
    paragraphs: Paragraph[]
  },
  metaData: ComposedPage['metaData'] & {
    adaAutoNative?: boolean
  }
}

const posArticleMobile2 = 2
const posListLatestMobile2 = 2
const posListLatestMobile3 = 14
let posListNativeCrossbarTop = 5
let dynamicAdSlotsEnabled = false

const isArticleOrArticleVersion = (pageType: string) =>
  pageType === 'article' || pageType === 'articleversion'

function addNativeCrossbarTop (paragraphs: Paragraph[], pageType: string): Paragraph[] {
  let newParagraphs: Paragraph[] = []

  const addAdaNativeCrossbarParagraph = {
    paragraphType: 'adaNativeCrossbar',
    adaNativeCrossbar: { position: 'top' }
  } as Paragraph

  if (isArticleOrArticleVersion(pageType)) {
    newParagraphs = paragraphs
    newParagraphs.push(addAdaNativeCrossbarParagraph)
  } else {
    let count = 0
    let added = false

    if (dynamicAdSlotsEnabled) {
      posListNativeCrossbarTop = 4
    }

    paragraphs.forEach(function (value) {
      if ((!dynamicAdSlotsEnabled && value.paragraphType !== 'adaAdSlot') ||
        (dynamicAdSlotsEnabled && value.paragraphType === 'teaserGroup')) {
        count++
      }

      newParagraphs.push(value)

      if (count >= (posListNativeCrossbarTop - 1) && !added && !['horizontalLine', 'interline'].includes(value.paragraphType)) {
        newParagraphs.push(addAdaNativeCrossbarParagraph)
        added = true
      }
    })
  }

  return newParagraphs
}

function addTopmobile (paragraphs: Paragraph[], pageType: string): Paragraph[] {
  const newParagraphs: Paragraph[] = []
  let count = 0
  let added = 0

  const addParagraphMobile2 = {
    paragraphType: 'adaAdSlot',
    adaAdSlot: { slotName: 'mobile', slotId: 2 }
  } as Paragraph
  const addParagraphMobile3 = {
    paragraphType: 'adaAdSlot',
    adaAdSlot: { slotName: 'mobile', slotId: 3 }
  } as Paragraph

  if (isArticleOrArticleVersion(pageType)) {
    added = 0
    paragraphs.forEach(function (value) {
      if (value.paragraphType !== 'horizontalLine') {
        count++
      }

      newParagraphs.push(value)

      if (count >= (posArticleMobile2 - 1) && added === 0) {
        newParagraphs.push(addParagraphMobile2)
        added++
      }
    })

    if (!dynamicAdSlotsEnabled) {
      newParagraphs.push(addParagraphMobile3)
    }
  } else {
    paragraphs.forEach(function (value) {
      if (value.paragraphType === 'teaserGroup') {
        const numTeaser = value.teaserGroup!.teaserGroupType === 'bx-teaser-container--slider' ? 3 : (value.teaserGroup!.teasers as unknown[]).length
        newParagraphs.push(value)
        if (count >= posListLatestMobile2 && added === 0) {
          newParagraphs.push(addParagraphMobile2)
          added++
        }
        if (!dynamicAdSlotsEnabled && (count + numTeaser) > posListLatestMobile3 && added === 1) {
          newParagraphs.push(addParagraphMobile3)
          added++
        }
        count = count + numTeaser
      } else {
        if (count >= posListLatestMobile2 && added === 0) {
          newParagraphs.push(addParagraphMobile2)
          added++
        }

        newParagraphs.push(value)
        count++
      }
    })
  }

  return newParagraphs
}

function addOutstreamAd (paragraphs: Paragraph[], placementLogic: string): Paragraph[] {
  let newParagraphs: Paragraph[] = []

  const addParagraphOutstream = {
    paragraphType: 'adaAdSlot',
    adaAdSlot: { slotName: 'outstream_div', slotId: 0 }
  } as Paragraph

  if (isArticleOrArticleVersion(placementLogic)) {
    let added = false
    paragraphs.forEach(function (value) {
      newParagraphs.push(value)

      if (value.paragraphType === 'text' && !added) {
        newParagraphs.push(addParagraphOutstream)
        added = true
      }
    })

    if (!added) {
      newParagraphs.push(addParagraphOutstream)
    }
  } else {
    newParagraphs = paragraphs
  }

  return newParagraphs
}

function RenamePageableTeaserGroupToTeaserGroup (paragraph: Paragraph): void {
  paragraph.paragraphType = 'teaserGroup'
  paragraph.teaserGroup = paragraph.pageableTeaserGroup
  delete paragraph.pageableTeaserGroup
}

function splitStructureTermTeaserGroups (data: PageData): PageData {
  const maxTeaserCountPerGroup: Record<string, number> = {
    'bx-teaser-container--full-width-two-cols': 2,
    'bx-teaser-container--full-width': 1,
    'bx-teaser-container--one-half-width': 2,
    'bx-teaser-container--one-third-width': 3,
    'bx-teaser-container--one-fourth-width': 4,
    'bx-teaser-container--one-sixth-width': 6,
    'bx-teaser-container--one-half-width-two-cols': 1,
    'bx-teaser-container--one-half-width-text-only': 2
  }

  let allowedTeaserCount = 0
  const paragraphs = data.body.paragraphs
  const newParagraphs: Paragraph[] = []

  paragraphs.forEach(function (paragraph) {
    // Add Logic based on teaserGroup Paragraphs, so we'll rename this group
    // so the given ad logic will affect also pages with pagination
    if (paragraph.paragraphType === 'pageableTeaserGroup') {
      RenamePageableTeaserGroupToTeaserGroup(paragraph)
    }
    if (paragraph.paragraphType === 'teaserGroup') {
      const groupTypeClasses = paragraph.teaserGroup!.teaserGroupType!.split(' ')
      groupTypeClasses.forEach(function (cls) {
        if (maxTeaserCountPerGroup[cls] !== undefined) {
          allowedTeaserCount = maxTeaserCountPerGroup[cls]
        }
      })

      const groupTeaser = paragraph.teaserGroup!.teasers
      let teaserCount = 0
      let newTeaserGroup = {} as Paragraph
      let groupMask = paragraph
      groupMask.teaserGroup!.teasers = []

      groupTeaser?.forEach(function (teaser) {
        teaserCount++
        if (teaserCount === 1) {
          // for the first split group we use the mask from the given group
          // to keep existing properties which we'll skip for further splits
          newTeaserGroup = groupMask
        }

        newTeaserGroup.teaserGroup!.teasers!.push(teaser)

        if (teaserCount === allowedTeaserCount) {
          newParagraphs.push(newTeaserGroup)
          // for further splits we use a minimum skeleton and only keep
          // the groupType
          groupMask = {
            paragraphType: 'teaserGroup',
            teaserGroup: {
              teaserGroupType: newTeaserGroup.teaserGroup!.teaserGroupType,
              teasers: []
            }
          } as Paragraph
          teaserCount = 0
        }
      })
      // if the given amount of teasers can't be split in equal groups
      // we'll have to add the unclosed group
      // TODO fits for madame, for later usage we need to modify the teaserGroupType so the group fits to the num of teaser
      if (teaserCount !== 0) {
        newParagraphs.push(newTeaserGroup)
      }
    } else {
      newParagraphs.push(paragraph)
    }
  })

  data.body.paragraphs = newParagraphs
  return data
}

function embedAds (data: ComposedPage | null, rsConfig?: RsConfig): ComposedPage | null {
  if (!data) return null

  const pageData = data as PageData

  if (!pageData.body.paragraphs || !!pageData.metaData?.adaAutoNative) {
    return data
  }
  dynamicAdSlotsEnabled = rsConfig?.adSlotUsage?.dynamicAdSlots?.enabled ?? false
  // check if we've already embedded dynamic ad-slots
  if (pageData.pageMeta.adSlots === undefined) {
    pageData.pageMeta.pageType = pageData.pageMeta.pageType!.toLowerCase()
    pageData.pageMeta.adSlots = {
      embedded: false,
      info: {}
    }
    if (pageData.pageMeta.pageType === 'structureterm' && (pageData.pageMeta.page ?? 1) > 1) {
      // rename pageable teaser groups
      splitStructureTermTeaserGroups(pageData)
    }
    const placementLogic = pageData.metaData?.behaveLikeArticle ? 'article' : pageData.pageMeta.pageType
    const initialParagraphCount = pageData.pageMeta.adSlots.info.initialParagraphCount = pageData.body.paragraphs.length

    for (let i = 0; i < initialParagraphCount; i++) {
      pageData.body.paragraphs[i].listOrderPosition = i
    }

    // embed ad-slots based on ad-slot type
    if (!pageData?.metaData?.disallowPosterAds && !pageData?.metaData?.disallowAds && !dynamicAdSlotsEnabled) {
      pageData.body.paragraphs = addOutstreamAd(pageData.body.paragraphs, placementLogic)
    }

    if (!dynamicAdSlotsEnabled) {
      pageData.body.paragraphs = addTopmobile(pageData.body.paragraphs, placementLogic)
      if (!pageData?.metaData?.advertorial) {
        pageData.body.paragraphs = addNativeCrossbarTop(pageData.body.paragraphs, placementLogic)
      }
    }

    pageData.pageMeta.adSlots.embedded = true
  }
  return data
}

export function useAdHandler () {
  return { embedAds }
}
